import React, { useState, useEffect } from 'react';
import '../../Styles/Payments/CardElementStyle.css';

function CardElement({ onSubmit, onCancel, userId, amount, roundId, auctionId, auctionName }) {
    const [step, setStep] = useState(1); // Step 1: Card, Step 2: Info
    const [card, setCard] = useState({
        number: '',
        expMonth: '',
        expYear: '',
        cvc: '',
    });
    const [billing, setBilling] = useState({
        name: '',
        street: '',
        city: '',
        state: '',
        country: '',
        zip: ''
    });
    const [ipAddress, setIpAddress] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // Fetch IP address
    useEffect(() => {
        const fetchIp = async () => {
            try {
                const res = await fetch('https://api.ipify.org?format=json');
                const data = await res.json();
                setIpAddress(data.ip);
            } catch {
                setIpAddress('127.0.0.1');
            }
        };
        fetchIp();
    }, []);

    // const handleCardChange = (e) => {
    //     setCard({ ...card, [e.target.name]: e.target.value });
    // };

    const handleBillingChange = (e) => {
        setBilling({ ...billing, [e.target.name]: e.target.value });
    };

    const handleNext = () => {
        if (!card.number || !card.expMonth || !card.expYear || !card.cvc) {
            setError('Please complete all card fields');
            return;
        }
        setError('');
        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        // Validate billing fields
        const required = ['name', 'street', 'city', 'state', 'country', 'zip'];
        for (const field of required) {
            if (!billing[field]) {
                setError(`Please fill out ${field}`);
                setLoading(false);
                return;
            }
        }

        try {
            const res = await fetch('https://fqzh94i59u.us-east-2.awsapprunner.com/payments/payments', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-card-number': card.number.replace(/\s+/g, ''),
                    'x-card-exp-month': card.expMonth,
                    'x-card-exp-year': card.expYear,
                    'x-card-cvc': card.cvc,
                },
                body: JSON.stringify({
                    provider: "rio",
                    terminalId: 24,
                    amount: amount.toFixed(2),
                    ip: { address: ipAddress },
                    metadata: {
                        userId,
                        roundId,
                        auctionId,
                        auctionName
                    }
                }),
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || 'Payment failed');
            
            alert("Payment successful!");
            setSuccess("");
            setStep(1);
            await onSubmit();
        } catch (err) {
            console.error('Payment error:', err);
            setError('Payment failed. Please try again.');
        }

        setLoading(false);
    };

    const handleCardChange = (e) => {
        const { name, value } = e.target;
        let cleaned = value.replace(/\D/g, ''); // Remove non-numeric
    
        if (name === 'number') {
            cleaned = cleaned.slice(0, 16);
            const spaced = cleaned.replace(/(.{4})/g, '$1 ').trim();
            setCard({ ...card, [name]: spaced });
        } else if (name === 'expMonth') {
            cleaned = cleaned.slice(0, 2);
            const month = cleaned > 12 ? '12' : cleaned;
            setCard({ ...card, [name]: month });
        } else if (name === 'expYear') {
            cleaned = cleaned.slice(0, 4);
            setCard({ ...card, [name]: cleaned });
        } else if (name === 'cvc') {
            cleaned = cleaned.slice(0, 4);
            setCard({ ...card, [name]: cleaned });
        } else {
            setCard({ ...card, [name]: value });
        }
    };    

    return (
        <form onSubmit={handleSubmit} className="card-form">
            <div className="borderBox">
                <h3>{step === 1 ? 'Enter Card Details' : 'Enter Billing Details'}</h3>
                <p className="secondaryColor">${amount} for {auctionName} {roundId}</p>
                <p className="secondaryColor">BCA does not store your card info anywhere at any stage.</p>
            </div>

            {step === 1 && (
                <>
                    <label>Card Number
                        <input
                            name="number"
                            value={card.number}
                            onChange={handleCardChange}
                            placeholder="4111 1111 1111 1111"
                            inputMode="numeric"
                            required
                        />
                    </label>

                    <div className="horizontalFlex">
                        <label>Expiry Month
                            <input
                                name="expMonth"
                                value={card.expMonth}
                                onChange={handleCardChange}
                                placeholder="MM"
                                inputMode="numeric"
                                required
                            />
                        </label>
                        <label>Expiry Year
                            <input
                                name="expYear"
                                value={card.expYear}
                                onChange={handleCardChange}
                                placeholder="YYYY"
                                inputMode="numeric"
                                required
                            />
                        </label>
                    </div>

                    <label>CVC
                        <input
                            name="cvc"
                            value={card.cvc}
                            onChange={handleCardChange}
                            placeholder="123"
                            inputMode="numeric"
                            required
                        />
                    </label>

                    {error && <p className="error">{error}</p>}

                    <button type="button" className="payBtn" onClick={handleNext}>Next</button>
                    <button type="button" className="cancelBtn" onClick={onCancel}>Cancel</button>
                </>
            )}

            {step === 2 && (
                <>
                    <label>Name on Card
                        <input name="name" value={billing.name} onChange={handleBillingChange} required />
                    </label>

                    <label>Street Address
                        <input name="street" value={billing.street} onChange={handleBillingChange} required />
                    </label>

                    <label>City
                        <input name="city" value={billing.city} onChange={handleBillingChange} required />
                    </label>

                    <div className="horizontalFlex">
                        <label>State / Province
                            <input name="state" value={billing.state} onChange={handleBillingChange} required />
                        </label>

                        <label>ZIP / Postal Code
                            <input name="zip" value={billing.zip} onChange={handleBillingChange} required />
                        </label>
                    </div>

                    <label>Country
                        <input name="country" value={billing.country} onChange={handleBillingChange} required />
                    </label>

                    {error && <p className="error">{error}</p>}
                    {success && <p className="success" style={{ color: 'green' }}>{success}</p>}

                    <button type="submit" className="payBtn" disabled={loading}>
                        {loading ? 'Processing...' : `Pay $${amount}`}
                    </button>
                    <button type="button" className="cancelBtn" onClick={() => setStep(1)}>Back</button>
                </>
            )}
        </form>
    );
}

export default CardElement;
